/**
 * Transient banner that appears on the host TV when Mr. X reveals his
 * position on a reveal round (3 / 8 / 13 / 18 / 24).
 *
 * Watches the public mrx log; when a new entry arrives whose `revealedStation`
 * is non-null, fades a banner in for a few seconds, then out. Self-managed —
 * no external trigger needed.
 */

import { Box, Text, Title } from '@mantine/core'
import { useEffect, useRef, useState } from 'react'
import type { GameState } from '@mdev/common'

const BANNER_DURATION_MS = 4500

interface Props {
  state: GameState
}

export function RevealBanner( { state }: Props ) {
  const [ active, setActive ] = useState<{ round: number; station: number } | null>( null )
  const lastSeenLengthRef = useRef( 0 )
  const timerRef = useRef<number | null>( null )

  useEffect( () => {
    const log = state.mrx.transportLog
    if ( log.length === lastSeenLengthRef.current ) return
    // New entries since last render. Look at the entries we just gained for a reveal.
    const newEntries = log.slice( lastSeenLengthRef.current )
    lastSeenLengthRef.current = log.length
    let reveal: typeof newEntries[ number ] | undefined
    for ( let i = newEntries.length - 1; i >= 0; i-- ) {
      if ( newEntries[ i ].revealedStation != null ) {
        reveal = newEntries[ i ]
        break
      }
    }
    if ( !reveal || reveal.revealedStation == null ) return

    setActive( { round: reveal.round, station: reveal.revealedStation } )
    if ( timerRef.current != null ) window.clearTimeout( timerRef.current )
    timerRef.current = window.setTimeout( () => setActive( null ), BANNER_DURATION_MS )
  }, [ state.mrx.transportLog ] )

  useEffect( () => () => {
    if ( timerRef.current != null ) window.clearTimeout( timerRef.current )
  }, [] )

  if ( !active ) return null

  return (
    <Box
      style={ {
        position: 'absolute',
        top: 60,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'linear-gradient(135deg, #FACC15 0%, #F59E0B 100%)',
        borderRadius: 16,
        padding: '20px 36px',
        boxShadow: '0 20px 60px rgba(250, 204, 21, 0.5), 0 0 0 4px rgba(250, 204, 21, 0.25)',
        animation: 'reveal-pop 0.4s ease-out',
        pointerEvents: 'none',
        zIndex: 100,
      } }
    >
      <Text
        size="xs"
        c="#0B0F1A"
        fw={ 800 }
        tt="uppercase"
        ta="center"
        style={ { letterSpacing: 2 } }
      >
        Mr. X spotted — round { active.round }
      </Text>
      <Title
        order={ 1 }
        c="#0B0F1A"
        ta="center"
        style={ { fontSize: 56, fontWeight: 900, lineHeight: 1.1, marginTop: 4 } }
      >
        Station #{ active.station }
      </Title>
      <style>{ `
        @keyframes reveal-pop {
          0%   { transform: translateX(-50%) scale(0.5); opacity: 0; }
          60%  { transform: translateX(-50%) scale(1.08); opacity: 1; }
          100% { transform: translateX(-50%) scale(1); opacity: 1; }
        }
      ` }</style>
    </Box>
  )
}
